import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { EcosystemNav } from '../components/ui/ecosystem-nav';
import { ToolsPanel } from '../components/ui/tools-panel';
import { useAuth, SignInBanner } from '../components/ui/auth-components';
import { BelongingFooter } from '../components/ui/belonging-footer';
import { toast } from 'sonner';
import {
  GoldenSpiralIcon,
  LotusIcon,
  AncientSunIcon,
  MetatronIcon,
  MandalaIcon,
  TreeOfLifeIcon,
  BrandCheckerIcon,
  EmergencySOSIcon,
  StoryCompassIcon,
  TomeMachineIcon,
  MairyIcon,
  CompassIcon,
  // New Tome Machine specific icons
  GenesisSparkIcon,
  SoulMirrorIcon,
  RealmGateIcon,
  ConflictFireIcon,
  SacredScrollIcon,
  VoiceQuillIcon,
  SacredHeartIcon,
  VisionaryEyeIcon,
  AncientTomeIcon,
  SacredReelIcon,
  ThirdEyeIcon,
  SacredChevronRight,
  SacredChevronLeft,
  SacredCheckIcon,
  SacredPlusIcon,
  SacredSpiralIcon,
  SacredSparklesIcon,
  SacredDownloadIcon,
  SacredCopyIcon,
  SacredMenuIcon,
  SacredCloseIcon,
  SacredPanelOpenIcon,
  SacredPanelCloseIcon,
  SacredListIcon,
  SacredFocusIcon
} from '../components/ui/legendary-icons';
import { BrandChecker } from '../components/ui/brand-checker';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ELCHME_URL = 'https://elchme.com';

// ============ 8 COMPASS POINTS FOR STORYTELLING ============
// Each includes introspection prompts about belonging, identity, and vision
const TOME_SECTIONS = [
  {
    key: 'north',
    direction: 'N',
    title: 'Genesis & Core',
    subtitle: 'The Seed of Your Story',
    description: 'Every great story begins with a single truth. What fundamental question does your narrative answer?',
    icon: GenesisSparkIcon,
    color: '#fbbf24',
    gradient: 'from-amber-500 to-yellow-500',
    subElements: [
      { key: 'premise', title: 'The Premise', prompt: 'In one sentence, what is your story about? (e.g., "A farm boy discovers he\'s destined to save the galaxy")' },
      { key: 'controlling_idea', title: 'Controlling Idea', prompt: 'What is the core truth or theme your story explores? (e.g., "Love conquers fear" or "Power corrupts")' },
      { key: 'central_question', title: 'Central Question', prompt: 'What question does your story answer for the audience? What will they walk away contemplating?' },
      { key: 'genre_tone', title: 'Genre & Tone', prompt: 'What genre(s) does this belong to? What\'s the emotional tone - dark, hopeful, satirical, romantic?' },
      { key: 'logline', title: 'The Logline', prompt: 'Write a compelling 1-2 sentence hook that would appear on a book cover or movie poster.' },
      { key: 'author_why', title: '✨ Why YOU Must Tell This', prompt: 'What personal connection do you have to this story? Why are you the one who must write it? What belonging or longing in your own life does this story speak to?' }
    ]
  },
  {
    key: 'northeast',
    direction: 'NE',
    title: 'Character & Soul',
    subtitle: 'The Hero\'s Heart',
    description: 'Characters are the vessels through which readers experience your world. Who carries this story?',
    icon: SoulMirrorIcon,
    color: '#f97316',
    gradient: 'from-orange-500 to-amber-500',
    subElements: [
      { key: 'protagonist', title: 'The Protagonist', prompt: 'Who is your main character? What makes them unique, flawed, and compelling?' },
      { key: 'want_vs_need', title: 'Want vs. Need', prompt: 'What does your protagonist WANT (external goal) vs. what do they NEED (internal growth)?' },
      { key: 'ghost', title: 'The Ghost/Wound', prompt: 'What past trauma or "ghost" haunts your character? What lie do they believe about themselves?' },
      { key: 'character_arc', title: 'Character Arc', prompt: 'How will your character transform from beginning to end? What will they learn?' },
      { key: 'supporting_cast', title: 'Supporting Cast', prompt: 'Who are the key supporting characters? Mentor, love interest, best friend, rival?' },
      { key: 'author_reflection', title: '✨ Who Are You in This Story?', prompt: 'Which character aspect reflects your own journey? What parts of yourself are you exploring through these characters? Where do you belong in this narrative?' }
    ]
  },
  {
    key: 'east',
    direction: 'E',
    title: 'World & Setting',
    subtitle: 'The Realm',
    description: 'Every story lives in a world. Build the universe your characters inhabit.',
    icon: RealmGateIcon,
    color: '#10b981',
    gradient: 'from-emerald-500 to-teal-500',
    subElements: [
      { key: 'time_place', title: 'Time & Place', prompt: 'When and where does your story take place? Be specific about era, location, and atmosphere.' },
      { key: 'world_rules', title: 'World Rules', prompt: 'What are the rules of your world? (Physical laws, magic systems, social structures, technology level)' },
      { key: 'sensory_palette', title: 'Sensory Palette', prompt: 'What does this world look, sound, smell, taste, and feel like? Create a mood board in words.' },
      { key: 'social_context', title: 'Social Context', prompt: 'What are the politics, cultures, and power structures? What tensions exist in this society?' },
      { key: 'ordinary_world', title: 'The Ordinary World', prompt: 'What is normal life like for your protagonist before the story begins?' },
      { key: 'belonging_spaces', title: '✨ Spaces of Belonging', prompt: 'Where do characters feel they belong in this world? Where are they outsiders? What does "home" mean here?' }
    ]
  },
  {
    key: 'southeast',
    direction: 'SE',
    title: 'Conflict & Stakes',
    subtitle: 'The Challenge',
    description: 'Conflict is the engine of story. What forces oppose your hero, and what hangs in the balance?',
    icon: ConflictFireIcon,
    color: '#ef4444',
    gradient: 'from-red-500 to-rose-500',
    subElements: [
      { key: 'antagonist', title: 'The Antagonist', prompt: 'Who or what opposes your protagonist? What makes them a worthy adversary?' },
      { key: 'central_conflict', title: 'Central Conflict', prompt: 'What is the main conflict? Is it person vs. person, self, society, nature, or fate?' },
      { key: 'stakes', title: 'The Stakes', prompt: 'What does your protagonist stand to lose? Why should we care if they fail?' },
      { key: 'obstacles', title: 'Obstacles & Complications', prompt: 'What barriers stand between your hero and their goal? List major challenges they\'ll face.' },
      { key: 'ticking_clock', title: 'The Ticking Clock', prompt: 'Is there urgency? What deadline or pressure forces action?' },
      { key: 'internal_battle', title: '✨ The Inner War', prompt: 'What internal conflict mirrors the external one? What must your protagonist overcome within themselves?' }
    ]
  },
  {
    key: 'south',
    direction: 'S',
    title: 'Structure & Beats',
    subtitle: 'The Blueprint',
    description: 'Great stories follow invisible architecture. Map the skeleton of your narrative.',
    icon: SacredScrollIcon,
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-purple-500',
    subElements: [
      { key: 'opening_image', title: 'Opening Image', prompt: 'What is the first image/scene? This should establish tone and hint at the journey to come.' },
      { key: 'inciting_incident', title: 'Inciting Incident', prompt: 'What event disrupts the ordinary world and launches your protagonist into the story?' },
      { key: 'midpoint', title: 'The Midpoint', prompt: 'What happens at the center of your story? Often a false victory/defeat that raises stakes.' },
      { key: 'dark_night', title: 'Dark Night of the Soul', prompt: 'What is your protagonist\'s lowest moment? When all seems lost, what breaks them?' },
      { key: 'climax_resolution', title: 'Climax & Resolution', prompt: 'How does the final confrontation unfold? How is the world changed at the end?' },
      { key: 'transformation_moment', title: '✨ The Transformation', prompt: 'What is the precise moment your character finally changes? What realization or choice marks their transformation?' }
    ]
  },
  {
    key: 'southwest',
    direction: 'SW',
    title: 'Voice & Style',
    subtitle: 'The Craft',
    description: 'How you tell the story is as important as what you tell. Find your unique voice.',
    icon: VoiceQuillIcon,
    color: '#ec4899',
    gradient: 'from-pink-500 to-fuchsia-500',
    subElements: [
      { key: 'pov', title: 'Point of View', prompt: 'Who tells the story? First person, third limited, omniscient? Whose eyes do we see through?' },
      { key: 'narrative_voice', title: 'Narrative Voice', prompt: 'What is the voice like? Formal, casual, poetic, sparse, humorous, dark? Give examples.' },
      { key: 'dialogue_style', title: 'Dialogue Style', prompt: 'How do your characters speak? Regional dialects, formal speech, modern slang, witty banter?' },
      { key: 'pacing', title: 'Pacing & Rhythm', prompt: 'Is this a fast-paced thriller or a slow-burn literary piece? How do chapters/scenes flow?' },
      { key: 'signature_elements', title: 'Signature Elements', prompt: 'What unique stylistic flourishes define your writing? Recurring motifs, sentence structure, imagery?' },
      { key: 'authentic_voice', title: '✨ Your Authentic Voice', prompt: 'What makes YOUR writing voice unique? What stories, experiences, and perspectives only you can bring? What is the "you" in your prose?' }
    ]
  },
  {
    key: 'west',
    direction: 'W',
    title: 'Emotion & Theme',
    subtitle: 'The Heart',
    description: 'Stories that endure touch the soul. What emotional truth lives at the core of your narrative?',
    icon: SacredHeartIcon,
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-sky-500',
    subElements: [
      { key: 'emotional_journey', title: 'Emotional Journey', prompt: 'What emotions do you want readers to feel at key moments? Map the emotional arc.' },
      { key: 'themes', title: 'Deeper Themes', prompt: 'What larger ideas does your story explore? (Identity, mortality, love, justice, redemption)' },
      { key: 'symbols', title: 'Symbols & Motifs', prompt: 'What recurring symbols, images, or motifs reinforce your themes?' },
      { key: 'resonance', title: 'Universal Resonance', prompt: 'Why will this story matter to readers? What human experience does it illuminate?' },
      { key: 'catharsis', title: 'The Catharsis', prompt: 'What emotional release does your ending provide? How should readers feel closing the book?' },
      { key: 'belonging_theme', title: '✨ Belonging & Connection', prompt: 'How does your story explore belonging, loneliness, connection, or community? What does it say about the human need to be seen and understood?' }
    ]
  },
  {
    key: 'northwest',
    direction: 'NW',
    title: 'Vision & Legacy',
    subtitle: 'The Destination',
    description: 'Your story deserves to reach its audience. Define where this work belongs in the world.',
    icon: VisionaryEyeIcon,
    color: '#6366f1',
    gradient: 'from-indigo-500 to-violet-500',
    subElements: [
      { key: 'format', title: 'Format & Medium', prompt: 'Is this a novel, screenplay, graphic novel, documentary, limited series, memoir, or something else?' },
      { key: 'target_audience', title: 'Target Audience', prompt: 'Who is this for? Age group, demographics, interests? Who will love this most?' },
      { key: 'comparables', title: 'Comparable Works', prompt: 'What existing works is this similar to? "X meets Y" - help position your story.' },
      { key: 'unique_hook', title: 'Unique Hook', prompt: 'What makes your story different from everything else? Why this story, now?' },
      { key: 'vision', title: 'The Vision', prompt: 'What is your ultimate dream for this work? Bestseller? Film adaptation? Award winner? What success looks like.' },
      { key: 'legacy_intention', title: '✨ Your Legacy', prompt: 'What do you want readers to carry with them forever? What seed do you want to plant in their hearts? What change do you hope to create in the world?' }
    ]
  }
];

// Format options for the Tome Machine
const TOME_OUTPUT_OPTIONS = [
  { key: 'novel_outline', title: 'Novel Outline', description: 'Chapter-by-chapter breakdown with scene summaries', icon: AncientTomeIcon },
  { key: 'screenplay', title: 'Screenplay Treatment', description: 'Film/TV treatment with act structure and beats', icon: SacredReelIcon },
  { key: 'pitch_deck', title: 'Pitch Deck', description: 'Agent/Publisher pitch with synopsis and comparables', icon: VisionaryEyeIcon },
  { key: 'character_bible', title: 'Character Bible', description: 'Deep character profiles for all major players', icon: SoulMirrorIcon },
  { key: 'world_bible', title: 'World Bible', description: 'Complete setting guide and lore document', icon: RealmGateIcon },
  { key: 'first_chapter', title: 'First Chapter Draft', description: 'AI-generated opening chapter based on your inputs', icon: VoiceQuillIcon }
];

// ============ COMPASS ROSE VISUALIZATION ============
const StoryCompassRose = ({ sections, activeSection, onSectionClick, responses }) => {
  const getSectionProgress = (section) => {
    let filled = 0;
    let total = section.subElements.length;
    section.subElements.forEach(sub => {
      const key = `${section.key}_${sub.key}`;
      if (responses[key]?.trim()) filled++;
    });
    return Math.round((filled / total) * 100);
  };

  const positions = [
    { key: 'north', x: 50, y: 5 },      // N
    { key: 'northeast', x: 85, y: 15 }, // NE
    { key: 'east', x: 95, y: 50 },      // E
    { key: 'southeast', x: 85, y: 85 }, // SE
    { key: 'south', x: 50, y: 95 },     // S
    { key: 'southwest', x: 15, y: 85 }, // SW
    { key: 'west', x: 5, y: 50 },       // W
    { key: 'northwest', x: 15, y: 15 }  // NW
  ];

  return (
    <div className="relative w-full aspect-square max-w-[300px] mx-auto">
      {/* Outer circle */}
      <div className="absolute inset-0 rounded-full border-2 border-violet-500/30" />
      {/* Inner circle */}
      <div className="absolute inset-[30%] rounded-full border border-fuchsia-500/20 bg-gradient-to-br from-violet-600/10 to-fuchsia-600/10" />
      
      {/* Center icon */}
      <div className="absolute inset-[38%] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        >
          <StoryCompassIcon size={40} className="text-fuchsia-400/60" />
        </motion.div>
      </div>
      
      {/* Compass points */}
      {sections.map((section, idx) => {
        const pos = positions[idx];
        const isActive = activeSection === section.key;
        const progress = getSectionProgress(section);
        const Icon = section.icon;
        
        return (
          <motion.button
            key={section.key}
            onClick={() => onSectionClick(section.key)}
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all ${
              isActive ? 'z-10 scale-110' : 'hover:scale-105'
            }`}
            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              isActive 
                ? `bg-gradient-to-br ${section.gradient} shadow-lg shadow-${section.color}/30` 
                : 'bg-slate-800/80 border border-white/10 hover:border-white/30'
            }`}>
              <Icon size={20} className={isActive ? 'text-white' : 'text-white/60'} />
              
              {/* Progress ring */}
              <svg className="absolute inset-0 w-full h-full -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="22"
                  stroke={isActive ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="22"
                  stroke={progress === 100 ? '#10b981' : section.color}
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={`${(progress / 100) * 138} 138`}
                  strokeLinecap="round"
                />
              </svg>
            </div>
            
            {/* Direction label */}
            <div className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-bold ${
              isActive ? 'text-white' : 'text-white/40'
            }`}>
              {section.direction}
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

// ============ COMPASS NAVIGATION SIDEBAR ============
const TomeCompassNav = ({ sections, activeSection, onSectionClick, responses }) => {
  const getSectionProgress = (section) => {
    let filled = 0;
    let total = section.subElements.length;
    section.subElements.forEach(sub => {
      const key = `${section.key}_${sub.key}`;
      if (responses[key]?.trim()) filled++;
    });
    return Math.round((filled / total) * 100);
  };

  return (
    <div className="space-y-2">
      {sections.map((section) => {
        const isActive = activeSection === section.key;
        const progress = getSectionProgress(section);
        const Icon = section.icon;
        
        return (
          <motion.button
            key={section.key}
            onClick={() => onSectionClick(section.key)}
            className={`w-full p-3 rounded-xl text-left transition-all ${
              isActive 
                ? 'bg-gradient-to-r from-violet-600/30 to-fuchsia-600/30 border border-violet-400/50' 
                : 'bg-white/5 hover:bg-white/10 border border-white/10'
            }`}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${section.gradient} flex items-center justify-center`}>
                <span className="text-white text-xs font-bold">{section.direction}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Icon size={14} className="text-white/70" />
                  <h4 className="font-medium text-sm truncate text-white/90">{section.title}</h4>
                </div>
                <p className="text-xs text-white/50 truncate">{section.subtitle}</p>
              </div>
              <div className="text-right">
                <span className={`text-xs font-mono ${progress === 100 ? 'text-emerald-400' : 'text-white/40'}`}>
                  {progress}%
                </span>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

// ============ MAIN COMPONENT ============
const TomeMachine = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');  // Get project ID from URL query params
  const { isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState('north');
  const [activeSubElement, setActiveSubElement] = useState('premise');
  const [responses, setResponses] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showBrandChecker, setShowBrandChecker] = useState(false);
  const [showSignInBanner, setShowSignInBanner] = useState(true);
  const [toolsPanelCollapsed, setToolsPanelCollapsed] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [showOutputOptions, setShowOutputOptions] = useState(false);
  const [generatedTome, setGeneratedTome] = useState(null);
  const [showTomeModal, setShowTomeModal] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState('novel_outline');
  const [mobileViewMode, setMobileViewMode] = useState('vertical'); // 'vertical' (all sections) or 'focused' (single section)
  const [tasks, setTasks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('tome_machine_tasks') || '[]');
    } catch { return []; }
  });
  const [reminders, setReminders] = useState([]);

  // Load saved responses
  useEffect(() => {
    const saved = localStorage.getItem('tome_machine_responses');
    if (saved) {
      try {
        setResponses(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load saved responses');
      }
    }
  }, []);

  // Save responses
  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      localStorage.setItem('tome_machine_responses', JSON.stringify(responses));
    }
  }, [responses]);

  // Save tasks
  useEffect(() => {
    localStorage.setItem('tome_machine_tasks', JSON.stringify(tasks));
  }, [tasks]);

  const currentSection = TOME_SECTIONS.find(s => s.key === activeSection);
  const currentSubElement = currentSection?.subElements.find(s => s.key === activeSubElement);
  const responseKey = `${activeSection}_${activeSubElement}`;

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    let total = 0;
    let filled = 0;
    TOME_SECTIONS.forEach(section => {
      section.subElements.forEach(sub => {
        total++;
        const key = `${section.key}_${sub.key}`;
        if (responses[key]?.trim()) filled++;
      });
    });
    return Math.round((filled / total) * 100);
  }, [responses]);

  const handleSectionClick = (sectionKey) => {
    setActiveSection(sectionKey);
    const section = TOME_SECTIONS.find(s => s.key === sectionKey);
    if (section?.subElements.length > 0) {
      setActiveSubElement(section.subElements[0].key);
    }
    setShowMobileNav(false);
  };

  const handleContentChange = (value) => {
    setResponses(prev => ({ ...prev, [responseKey]: value }));
  };

  // Add task handler
  const handleAddTask = (task) => {
    const newTask = {
      id: Date.now().toString(),
      title: task.title || task,
      category: task.category || 'writing',
      priority: task.priority || 'medium',
      completed: false,
      createdAt: new Date().toISOString()
    };
    setTasks(prev => [...prev, newTask]);
    toast.success('Task added!');
  };

  // Add content to current textarea from AI/tools
  const handleAddToContent = (content) => {
    setResponses(prev => ({
      ...prev,
      [responseKey]: (prev[responseKey] || '') + '\n\n' + content
    }));
    toast.success('Added to your content!');
  };

  // Navigate between sub-elements
  const goToNextSubElement = () => {
    const currentSubIndex = currentSection.subElements.findIndex(s => s.key === activeSubElement);
    if (currentSubIndex < currentSection.subElements.length - 1) {
      setActiveSubElement(currentSection.subElements[currentSubIndex + 1].key);
    } else {
      const currentSectionIndex = TOME_SECTIONS.findIndex(s => s.key === activeSection);
      if (currentSectionIndex < TOME_SECTIONS.length - 1) {
        const nextSection = TOME_SECTIONS[currentSectionIndex + 1];
        setActiveSection(nextSection.key);
        setActiveSubElement(nextSection.subElements[0].key);
      }
    }
  };

  const goToPrevSubElement = () => {
    const currentSubIndex = currentSection.subElements.findIndex(s => s.key === activeSubElement);
    if (currentSubIndex > 0) {
      setActiveSubElement(currentSection.subElements[currentSubIndex - 1].key);
    } else {
      const currentSectionIndex = TOME_SECTIONS.findIndex(s => s.key === activeSection);
      if (currentSectionIndex > 0) {
        const prevSection = TOME_SECTIONS[currentSectionIndex - 1];
        setActiveSection(prevSection.key);
        setActiveSubElement(prevSection.subElements[prevSection.subElements.length - 1].key);
      }
    }
  };

  // Generate Tome
  const generateTome = async (outputType) => {
    setIsGenerating(true);
    setSelectedOutput(outputType);
    setShowOutputOptions(false);

    try {
      const response = await fetch(`${API_URL}/api/tome/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses,
          output_type: outputType
        })
      });

      if (!response.ok) throw new Error('Generation failed');
      
      const data = await response.json();
      setGeneratedTome(data);
      setShowTomeModal(true);
      toast.success('Your tome has been generated!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy tome to clipboard
  const copyTome = () => {
    if (generatedTome?.content) {
      navigator.clipboard.writeText(generatedTome.content);
      toast.success('Copied to clipboard!');
    }
  };

  // Download tome
  const downloadTome = () => {
    if (generatedTome?.content) {
      const blob = new Blob([generatedTome.content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Tome_${selectedOutput}_${Date.now()}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Downloaded!');
    }
  };

  // Check if current element is an introspection element
  const isIntrospectionElement = currentSubElement?.key?.includes('author') || 
    currentSubElement?.key?.includes('belonging') || 
    currentSubElement?.key?.includes('legacy') ||
    currentSubElement?.key?.includes('authentic') ||
    currentSubElement?.key?.includes('reflection') ||
    currentSubElement?.key?.includes('transformation') ||
    currentSubElement?.key?.includes('internal');

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 50%, #1e1b4b 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/10" style={{ background: 'rgba(30, 27, 75, 0.95)' }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-violet-600 to-fuchsia-600">
                <TomeMachineIcon size={22} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-lg font-semibold text-white group-hover:text-fuchsia-300 transition-colors">Tome Machine</h1>
                  <Badge className="bg-fuchsia-500/20 text-fuchsia-300 text-[10px]">Story Studio</Badge>
                </div>
                <p className="text-xs text-white/50">{overallProgress}% Complete • 8-Point Story Compass</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Quick Tool Icons - Premium styling */}
              <div className="flex items-center gap-2 mr-3">
                <button
                  onClick={() => setShowBrandChecker(true)}
                  className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 via-orange-500/15 to-yellow-500/20 border border-amber-400/30 hover:border-amber-400/60 transition-all duration-300 flex items-center justify-center shadow-lg shadow-amber-500/10 hover:shadow-amber-500/20"
                  title="Brand Name Checker"
                  data-testid="brand-checker-icon"
                >
                  <BrandCheckerIcon size={20} className="text-amber-300 group-hover:text-amber-200 transition-colors" />
                </button>
                
                <a
                  href={ELCHME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500/20 via-red-500/15 to-pink-500/20 border border-rose-400/30 hover:border-rose-400/60 transition-all duration-300 flex items-center justify-center shadow-lg shadow-rose-500/10 hover:shadow-rose-500/20"
                  title="Emergency Empathy Alchemy - elchme.com"
                  data-testid="emergency-sos-icon"
                >
                  <EmergencySOSIcon size={20} className="text-rose-300 group-hover:text-rose-200 transition-colors" />
                </a>
              </div>

              {/* Generate Dropdown */}
              <div className="relative">
                <Button
                  size="sm"
                  onClick={() => setShowOutputOptions(!showOutputOptions)}
                  disabled={isGenerating || overallProgress < 10}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white border-0"
                >
                  {isGenerating ? <SacredSpiralIcon size={14} className="animate-spin mr-2" /> : <SacredSparklesIcon size={14} className="mr-2" />}
                  Generate
                  <SacredChevronRight size={14} className={`ml-1 transition-transform ${showOutputOptions ? 'rotate-90' : ''}`} />
                </Button>
                
                <AnimatePresence>
                  {showOutputOptions && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-2 w-80 bg-slate-800 rounded-xl border border-white/10 shadow-2xl overflow-hidden z-50"
                    >
                      <div className="p-2">
                        <p className="text-xs text-white/50 px-3 py-2">Choose your tome format:</p>
                        {TOME_OUTPUT_OPTIONS.map((option) => (
                          <button
                            key={option.key}
                            onClick={() => generateTome(option.key)}
                            className="w-full p-3 rounded-lg text-left hover:bg-white/10 transition-colors flex items-start gap-3"
                          >
                            <option.icon size={18} className="text-fuchsia-400 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-white">{option.title}</p>
                              <p className="text-xs text-white/50">{option.description}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Tools Panel Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setToolsPanelCollapsed(!toolsPanelCollapsed)}
                className="hidden lg:flex text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/20 border border-fuchsia-500/30"
              >
                {toolsPanelCollapsed ? <SacredPanelOpenIcon size={18} /> : <SacredPanelCloseIcon size={18} />}
              </Button>
              
              {/* Mobile Tools Panel Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowToolsPanel(!showToolsPanel)}
                className="lg:hidden text-fuchsia-300 hover:text-fuchsia-200"
              >
                <MairyIcon size={20} />
              </Button>
              
              {/* Mobile Nav Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMobileNav(!showMobileNav)}
                className="lg:hidden text-white"
              >
                {showMobileNav ? <SacredCloseIcon size={20} /> : <SacredMenuIcon size={20} />}
              </Button>
            </div>
          </div>
        </div>
        
        {!isAuthenticated && showSignInBanner && (
          <SignInBanner onClose={() => setShowSignInBanner(false)} />
        )}
      </header>

      {/* Mobile View Mode Toggle - Only visible on mobile */}
      <div className="lg:hidden sticky top-[60px] z-30 bg-slate-900/95 backdrop-blur-md border-b border-white/10 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant={mobileViewMode === 'vertical' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMobileViewMode('vertical')}
              className={mobileViewMode === 'vertical' 
                ? 'bg-fuchsia-600 text-white text-xs' 
                : 'text-white/60 text-xs'}
            >
              <SacredListIcon size={14} className="mr-1" />
              All Sections
            </Button>
            <Button
              variant={mobileViewMode === 'focused' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMobileViewMode('focused')}
              className={mobileViewMode === 'focused' 
                ? 'bg-fuchsia-600 text-white text-xs' 
                : 'text-white/60 text-xs'}
            >
              <SacredFocusIcon size={14} className="mr-1" />
              Focus Mode
            </Button>
          </div>
          <Badge className="bg-fuchsia-500/20 text-fuchsia-300 text-[10px]">
            {overallProgress}% Complete
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        <div className="container mx-auto px-4 py-6">
          
          {/* Mobile Vertical Scroll View - Shows all sections */}
          {mobileViewMode === 'vertical' && (
            <div className="lg:hidden space-y-6 pb-24">
              {TOME_SECTIONS.map((section, sectionIdx) => (
                <div key={section.key} className="space-y-4">
                  {/* Section Header */}
                  <Card className="bg-white/5 backdrop-blur border-white/10 overflow-hidden">
                    <div className={`h-1.5 bg-gradient-to-r ${section.gradient}`} />
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${section.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
                          <section.icon size={24} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Badge className="bg-white/10 text-white/70 text-[10px]">{section.direction}</Badge>
                            <span className="text-xs text-emerald-400">
                              {section.subElements.filter(se => responses[`${section.key}_${se.key}`]?.trim()).length}/{section.subElements.length}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-white truncate">{section.title}</h3>
                          <p className="text-xs text-white/50 truncate">{section.subtitle}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Sub-Elements for this section */}
                  <div className="space-y-3 pl-2">
                    {section.subElements.map((sub, subIdx) => {
                      const key = `${section.key}_${sub.key}`;
                      const hasContent = responses[key]?.trim();
                      const isIntrospective = sub.title.includes('✨');
                      
                      return (
                        <Card 
                          key={sub.key} 
                          className={`backdrop-blur border-white/10 ${
                            isIntrospective 
                              ? 'bg-gradient-to-br from-amber-500/10 to-rose-500/10 border-amber-500/20' 
                              : 'bg-white/5'
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-2 mb-2">
                              {hasContent ? (
                                <SacredCheckIcon size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                              ) : (
                                <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                                  isIntrospective ? 'border-amber-400/50' : 'border-white/30'
                                }`} />
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className={`text-sm font-medium ${
                                  isIntrospective ? 'text-amber-300' : 'text-white'
                                }`}>
                                  {sub.title}
                                </h4>
                                <p className={`text-xs mt-0.5 ${
                                  isIntrospective ? 'text-amber-200/60' : 'text-white/50'
                                }`}>
                                  {sub.prompt}
                                </p>
                              </div>
                            </div>
                            <Textarea
                              value={responses[key] || ''}
                              onChange={(e) => {
                                const newResponses = { ...responses, [key]: e.target.value };
                                setResponses(newResponses);
                                localStorage.setItem('tome_machine_responses', JSON.stringify(newResponses));
                              }}
                              placeholder={isIntrospective 
                                ? "Take your time... What truth wants to emerge?" 
                                : "Write here..."}
                              className={`min-h-[100px] text-sm border-white/10 text-white placeholder:text-white/30 focus:border-fuchsia-500/50 ${
                                isIntrospective ? 'bg-amber-500/10' : 'bg-black/30'
                              }`}
                            />
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  
                  {/* Section Divider */}
                  {sectionIdx < TOME_SECTIONS.length - 1 && (
                    <div className="flex items-center gap-4 py-4">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      <GoldenSpiralIcon size={20} className="text-fuchsia-400/50" />
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    </div>
                  )}
                </div>
              ))}
              
              {/* Generate Button at bottom for mobile */}
              <div className="fixed bottom-4 left-4 right-4 z-40">
                <Button
                  onClick={() => setShowOutputOptions(true)}
                  disabled={isGenerating || overallProgress < 10}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white shadow-xl shadow-fuchsia-500/30"
                >
                  {isGenerating ? <SacredSpiralIcon size={16} className="animate-spin mr-2" /> : <SacredSparklesIcon size={16} className="mr-2" />}
                  Generate Your Tome ({overallProgress}% Complete)
                </Button>
              </div>
            </div>
          )}

          {/* Desktop & Mobile Focused View - Panel-based layout */}
          <div className={`${mobileViewMode === 'vertical' ? 'hidden lg:grid' : 'grid'} gap-6 ${toolsPanelCollapsed ? 'lg:grid-cols-[280px_1fr]' : 'lg:grid-cols-[280px_1fr_360px]'}`}>
            
            {/* Left Sidebar - Compass Navigation */}
            <aside className="hidden lg:block space-y-6">
              {/* Compass Rose Visualization */}
              <Card className="bg-white/5 backdrop-blur border-white/10">
                <CardContent className="p-4">
                  <StoryCompassRose
                    sections={TOME_SECTIONS}
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                    responses={responses}
                  />
                </CardContent>
              </Card>
              
              {/* Section List */}
              <Card className="bg-white/5 backdrop-blur border-white/10">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white flex items-center gap-2">
                    <CompassIcon size={18} className="text-fuchsia-400" />
                    Story Compass
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TomeCompassNav
                    sections={TOME_SECTIONS}
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                    responses={responses}
                  />
                </CardContent>
              </Card>
            </aside>

            {/* Main Content Area */}
            <main className="space-y-6">
              {/* Section Header */}
              <Card className="bg-white/5 backdrop-blur border-white/10 overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${currentSection?.gradient}`} />
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${currentSection?.gradient} flex items-center justify-center shadow-lg`}>
                      {currentSection && <currentSection.icon size={28} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-white/10 text-white/80 text-xs">{currentSection?.direction}</Badge>
                        <h2 className="text-xl font-bold text-white">{currentSection?.title}</h2>
                      </div>
                      <p className="text-sm text-white/60 mb-3">{currentSection?.subtitle}</p>
                      <p className="text-sm text-white/80">{currentSection?.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sub-Elements Navigation */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {currentSection?.subElements.map((sub, idx) => {
                  const key = `${activeSection}_${sub.key}`;
                  const hasContent = responses[key]?.trim();
                  const isActive = activeSubElement === sub.key;
                  const isIntrospective = sub.title.includes('✨');
                  
                  return (
                    <Button
                      key={sub.key}
                      variant={isActive ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setActiveSubElement(sub.key)}
                      className={`whitespace-nowrap ${
                        isActive 
                          ? isIntrospective
                            ? 'bg-gradient-to-r from-amber-600 to-rose-600 text-white'
                            : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white' 
                          : hasContent 
                            ? 'text-emerald-300 border-emerald-500/30' 
                            : isIntrospective
                              ? 'text-amber-300/70 border-amber-500/20'
                              : 'text-white/60 border-white/10'
                      }`}
                    >
                      {hasContent && <SacredCheckIcon size={12} className="mr-1" />}
                      {sub.title}
                    </Button>
                  );
                })}
              </div>

              {/* Content Input */}
              <Card className={`backdrop-blur border-white/10 ${isIntrospectionElement ? 'bg-gradient-to-br from-amber-500/5 to-rose-500/5' : 'bg-white/5'}`}>
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    {isIntrospectionElement ? (
                      <>
                        <ThirdEyeIcon size={18} className="text-amber-400" />
                        <span className="text-amber-300">{currentSubElement?.title}</span>
                      </>
                    ) : (
                      <>
                        <VoiceQuillIcon size={18} className="text-fuchsia-400" />
                        {currentSubElement?.title}
                      </>
                    )}
                  </CardTitle>
                  <p className={`text-sm mt-1 ${isIntrospectionElement ? 'text-amber-200/70' : 'text-white/60'}`}>
                    {currentSubElement?.prompt}
                  </p>
                  {isIntrospectionElement && (
                    <p className="text-xs text-amber-300/50 mt-2 italic">
                      This is a moment for deep reflection. Your authentic voice matters here.
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={responses[responseKey] || ''}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder={isIntrospectionElement 
                      ? "Take your time with this one... What truth wants to emerge?" 
                      : "Begin writing here... Let your imagination flow."}
                    className={`min-h-[200px] border-white/10 text-white placeholder:text-white/30 focus:border-fuchsia-500/50 ${
                      isIntrospectionElement ? 'bg-amber-500/5' : 'bg-black/20'
                    }`}
                  />
                  
                  {/* Quick Actions */}
                  <div className="flex items-center gap-2 mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAddTask({ 
                        title: `Complete: ${currentSubElement?.title}`, 
                        category: 'writing' 
                      })}
                      className="text-white/50 hover:text-white hover:bg-white/10"
                    >
                      <SacredPlusIcon size={14} className="mr-1" />
                      Add to Tasks
                    </Button>
                  </div>
                  
                  {/* Navigation Buttons */}
                  <div className="flex justify-between mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToPrevSubElement}
                      className="text-white/60 hover:text-white"
                    >
                      <SacredChevronLeft size={16} className="mr-1" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      onClick={goToNextSubElement}
                      className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white"
                    >
                      Next
                      <SacredChevronRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </main>

            {/* Right Sidebar - Tools Panel */}
            {!toolsPanelCollapsed && (
              <aside className="hidden lg:block">
                <ToolsPanel
                  responses={responses}
                  activeSection={activeSection}
                  activeSubElement={activeSubElement}
                  currentPrompt={currentSubElement?.prompt || ''}
                  onAddContent={handleAddToContent}
                  onAddTask={handleAddTask}
                  onToggleReminder={(reminder) => {
                    setReminders(prev => 
                      prev.map(r => r.id === reminder.id ? {...r, completed: !r.completed} : r)
                    );
                  }}
                  projectId="tome"
                  projectName="Tome Machine"
                  reminders={reminders}
                  onUpdateReminders={setReminders}
                />
              </aside>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Sheet */}
      <AnimatePresence>
        {showMobileNav && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowMobileNav(false)} />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-r border-white/20 overflow-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-slate-900/95 backdrop-blur-md border-b border-white/10 p-4 flex items-center justify-between">
                <h3 className="font-heading text-lg text-white flex items-center gap-2">
                  <StoryCompassIcon size={20} className="text-fuchsia-400" />
                  Story Compass
                </h3>
                <button 
                  onClick={() => setShowMobileNav(false)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                >
                  <SacredCloseIcon size={20} />
                </button>
              </div>
              <div className="p-4">
                <TomeCompassNav
                  sections={TOME_SECTIONS}
                  activeSection={activeSection}
                  onSectionClick={handleSectionClick}
                  responses={responses}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Tools Panel */}
      <AnimatePresence>
        {showToolsPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowToolsPanel(false)} />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-slate-900 border-t border-white/10 rounded-t-2xl shadow-2xl"
              style={{ maxHeight: '85vh' }}
            >
              <div className="flex justify-center pt-2 pb-1">
                <div className="w-12 h-1 bg-white/20 rounded-full" />
              </div>
              <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(85vh - 20px)' }}>
                <ToolsPanel
                  responses={responses}
                  activeSection={activeSection}
                  activeSubElement={activeSubElement}
                  currentPrompt={currentSubElement?.prompt || ''}
                  onAddContent={handleAddToContent}
                  onAddTask={handleAddTask}
                  onToggleReminder={(reminder) => {
                    setReminders(prev => 
                      prev.map(r => r.id === reminder.id ? {...r, completed: !r.completed} : r)
                    );
                  }}
                  projectId="tome"
                  projectName="Tome Machine"
                  reminders={reminders}
                  onUpdateReminders={setReminders}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Generated Tome Modal */}
      <AnimatePresence>
        {showTomeModal && generatedTome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => setShowTomeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-slate-900 border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                    <Scroll size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      {TOME_OUTPUT_OPTIONS.find(o => o.key === selectedOutput)?.title || 'Your Tome'}
                    </h2>
                    <p className="text-xs text-white/50">
                      Generated {new Date().toLocaleString()} • {generatedTome.word_count || 0} words
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={copyTome} className="text-white/70 hover:text-white">
                    <SacredCopyIcon size={16} className="mr-2" />
                    Copy
                  </Button>
                  <Button size="sm" onClick={downloadTome} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white">
                    <SacredDownloadIcon size={16} className="mr-2" />
                    Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowTomeModal(false)} className="text-white/70">
                    <SacredCloseIcon size={20} />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                <div className="prose prose-invert max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed bg-transparent p-0 text-white/90">
                    {generatedTome.content}
                  </pre>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Brand Checker Modal */}
      <AnimatePresence>
        {showBrandChecker && (
          <BrandChecker
            onClose={() => setShowBrandChecker(false)}
          />
        )}
      </AnimatePresence>

      {/* Footer */}
      <BelongingFooter />

      {/* Ecosystem Nav */}
      <EcosystemNav currentSite="tome-machine" />
    </div>
  );
};

export default TomeMachine;
